import * as React from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import classnames from 'classnames';
import Compressor from 'compressorjs';

import Button, { CameraButton, DeleteButton } from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';

import { S3_CRM_SIGNED_URL_SUCCESS } from '@actions/s3';

import { getEmptyErrorState, getIndexedFileName, humanFileSize, MAX_ATTACHMENT_FILE_SIZE } from '@utils/AppUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import { getFormattedDeviceDetails } from '@utils/DeviceDetailsUtil';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';
import { track } from '@utils/MixPanelUtils';
import { s3uploadFile } from '@utils/S3Util';

import AttachmentImage from './AttachmentImage';

import './style.scss';

interface IFileUpload {
  details: File;
  data: string;
  progress: number;
  title: string;
  description: string;
  s3SavedUrl: string;
  error: {
    title: IFieldValidation;
    description: IFieldValidation;
    file: IFieldValidation;
  };
}

const cyberdyneValidator = new CyberdyneValidator();
const errMsgGen = new ErrorMessageGenerator();

export interface IAttachmentsModalProps {
  show: boolean;
  s3Operation?: S3Operation;
  onAdd?: (files: IAttachment[]) => void;
  onClose?: (e: any) => void;
  attachments?: IAttachment[];
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  title: string;
  agencyId: number;
  addError?: TAddError;
  trackLabel?: string;
  hasEditableFields?: boolean;
  getCRMSignedURL?: TGetCRMSignedURL;
  crmToken?: string;
}

interface IAttachmentsModalState {
  files: Map<string, IFileUpload>;
  isConfirmModalOpen?: boolean;
}

export default class AttachmentsModal extends React.Component<IAttachmentsModalProps, IAttachmentsModalState> {
  static defaultProps: Partial<IAttachmentsModalProps> = {
    acceptedFileTypes: [
      '.ai',
      '.aiff',
      '.bmp',
      '.doc',
      '.docx',
      '.eml',
      '.eps',
      '.gif',
      '.htm',
      '.html',
      '.heic',
      '.heif',
      '.jpe',
      '.jpeg',
      '.jpg',
      '.mp3',
      '.mp4',
      '.msg',
      '.pdf',
      '.png',
      '.ppt',
      '.pptx',
      '.ps',
      '.rtf',
      '.tif',
      '.tiff',
      '.txt',
      '.vcf',
      '.wav',
      '.xls',
      '.xlsx',
      '.zip',
      '.mov',
    ],
    maxFileSize: MAX_ATTACHMENT_FILE_SIZE,
    s3Operation: S3Operation.CASE_ATTACHMENT,
    hasEditableFields: true,
  };

  constructor(props: IAttachmentsModalProps) {
    super(props);
    this.state = {
      files: new Map(),
    };
  }

  componentWillReceiveProps(nextProps: IAttachmentsModalProps) {
    const hasModalOpened = nextProps.show !== this.props.show && nextProps.show;

    if (hasModalOpened) {
      this.setState({
        files: new Map(),
      });
    }
  }

  getAcceptedFileTypesString = (): string =>
    this.props.acceptedFileTypes ? this.props.acceptedFileTypes.join(', ') : '';

  onDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragExit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };

  onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.handleAttachmentChange(e);
  };

  getFileEmptyErrorState = () => ({
    title: getEmptyErrorState(),
    description: getEmptyErrorState(),
    file: getEmptyErrorState(),
  });

  getFileType = (file: IFileUpload) => {
    const fileNameSplit: string[] = (file.details && file.details.name.split('.')) || [];
    const fileNameExtension: string = fileNameSplit[fileNameSplit.length - 1];

    // TODO: this function should handle all file types, currently
    // only images is checked for.
    return /(heic|heif|jpe?g|png)$/.test(fileNameExtension.toLowerCase()) ? 'image' : 'others';
  };

  getThumb = (file: IFileUpload) => {
    const fileTypeToThumb = {
      image: (
        <AttachmentImage
          className={classnames('attachments-modal__saved-thumb')}
          src={file.data}
          alt={file.details?.name || 'Attachment preview'}
        />
      ),
      others: (
        <div className={classnames('attachments-modal__saved-thumb', 'attachments-modal__saved-thumb--others')} />
      ),
    };

    return fileTypeToThumb[this.getFileType(file)];
  };

  getValidFiles = (files: FileList) => {
    let validFiles = [];
    let hasInvalidFiles = false;

    for (const index in files) {
      if (!isNaN(+index)) {
        const splitText = files[index].name.split('.');
        const fileType = `.${splitText[splitText.length - 1]}`;

        if (this.props.acceptedFileTypes.includes(fileType.toLowerCase())) {
          validFiles = validFiles.concat(files[index]);
        } else {
          hasInvalidFiles = true;
        }
      }
    }

    if (hasInvalidFiles && this.props.addError) {
      this.props.addError(errMsgGen.getUnsupportedFileFormatMessage());
    }

    return validFiles;
  };

  handleAttachmentChange = (e) => {
    let files = null;
    if (e.dataTransfer) {
      files = this.getValidFiles(e.dataTransfer.files);
    } else if (e.target) {
      files = this.getValidFiles(e.target.files);
    }

    const isImage = (file) => {
      const split = file.name.split('.');
      const extension = split[split.length - 1];

      return /png|jpe?g/.test(extension.toLowerCase());
    };

    const onUploadProgress = (progressEvent, key) => {
      const progress: number = (progressEvent.loaded / progressEvent.total) * 100;

      const newFiles = new Map(this.state.files);

      if (newFiles.get(key)) {
        const currentFile = newFiles.get(key);

        newFiles.set(key, {
          ...currentFile,
          progress,
        });

        this.setState({
          files: newFiles,
        });
      }
    };

    const onUploadComplete = (response, key) => {
      const newFiles = new Map(this.state.files);

      // TODO: Change this to use XMLParser?
      if (newFiles.get(key) && response.documentElement && response.documentElement.getElementsByTagName('Location')) {
        const currentFile = newFiles.get(key);

        const s3SavedUrl = decodeURIComponent(
          response.documentElement.getElementsByTagName('Location')[0].childNodes[0].data
        );

        newFiles.set(key, {
          ...currentFile,
          s3SavedUrl,
          title: currentFile.title.trim().length ? currentFile.title : key,
        });

        this.setState({
          files: newFiles,
        });
      }
    };

    const onUploadFailed = (error, key) => {
      const newFiles = new Map(this.state.files);

      // TODO: Change this to use XMLParser?
      if (newFiles.get(key)) {
        const currentFile = newFiles.get(key);

        newFiles.set(key, {
          ...currentFile,
          progress: 100,
          s3SavedUrl: null,
          error: {
            ...currentFile.error,
            file: {
              errorState: IValidationState.ERROR,
              errorMessage: 'The upload was not successful, please try again.',
            },
          },
        });

        this.setState({
          files: newFiles,
        });
      }
    };

    files.map((file) => {
      const reader = new FileReader();
      const fileKey = getIndexedFileName(file.name);

      reader.onloadend = () => {
        const newFiles = new Map(this.state.files);
        newFiles.set(fileKey, {
          data: reader.result as string,
          details: file,
          progress: 0,
          title: '',
          description: '',
          s3SavedUrl: '',
          error: this.getFileEmptyErrorState(),
        });

        this.props.getCRMSignedURL(this.props.crmToken, this.props.s3Operation, fileKey).then((action) => {
          if (action.type === S3_CRM_SIGNED_URL_SUCCESS) {
            const body = new Map(action.response.requestBodyparams);

            if (isImage(file)) {
              // tslint:disable-next-line:no-unused-expression
              new Compressor(file, {
                quality: 0.7,
                success(compressedFile: Blob) {
                  s3uploadFile(body, compressedFile, (ev) => onUploadProgress(ev, fileKey)).then(
                    (response) => {
                      onUploadComplete(response, fileKey);
                    },
                    (error) => {
                      onUploadFailed(error, fileKey);
                    }
                  );
                },
              });
            } else {
              s3uploadFile(body, file, (ev) => onUploadProgress(ev, fileKey)).then(
                (response) => {
                  onUploadComplete(response, fileKey);
                },
                (error) => {
                  onUploadFailed(error, fileKey);
                }
              );
            }
          } else {
            onUploadFailed(action.error, fileKey);
          }
        });

        this.setState({
          files: newFiles,
        });
      };

      if (file.size < this.props.maxFileSize) {
        if (isImage(file)) {
          // tslint:disable-next-line:no-unused-expression
          new Compressor(file, {
            quality: 0.7,
            success: (compressedFile) => {
              reader.readAsDataURL(compressedFile);
            },
          });
        } else {
          reader.readAsDataURL(file);
        }
      } else {
        if (this.props.addError) {
          const maxFileSizeInMB = this.props.maxFileSize / (1024 * 1024);
          this.props.addError(errMsgGen.getMaxFileSizeLimitMessage(maxFileSizeInMB));
        }
      }
    });
    e.target.value = null;
  };

  isAddDisabled = () => {
    const files = this.state.files;
    if (files.size === 0) {
      return true;
    }

    for (const file of Array.from(files.values())) {
      if (file.progress !== 100 || !file.s3SavedUrl) {
        return true;
      }
    }

    return Array.from(files.keys()).some(
      (fileKey) => files.get(fileKey) && files.get(fileKey).error.file.errorState === IValidationState.ERROR
    );
  };

  onConfirmClose = (e) => {
    this.setState({
      isConfirmModalOpen: false,
      files: new Map(),
    });

    this.props.onClose(e);
  };

  onNotConfirmClose = () => {
    this.setState({
      isConfirmModalOpen: false,
    });
  };

  onClose = (e) => {
    if (this.state.files.size > 0) {
      this.setState({
        isConfirmModalOpen: true,
      });
    } else {
      this.props.onClose(e);
    }
  };

  isValidFile = (file: IFileUpload, fileName: string) => {
    const { description, title } = file;
    const error = this.getFileEmptyErrorState();
    let isValid = true;

    error.file = file.error.file;

    if (error.file.errorState === IValidationState.ERROR) {
      isValid = false;
    }

    if (cyberdyneValidator.isStringGreaterThanLength(description, 300)) {
      error.description.errorState = IValidationState.ERROR;
      error.description.errorMessage = errMsgGen.getCharacterLimitMessage('Description', 300);

      isValid = false;
    }

    if (this.props.hasEditableFields && cyberdyneValidator.isEmptyString(title)) {
      error.title.errorState = IValidationState.ERROR;
      error.title.errorMessage = errMsgGen.getMandatoryFieldMessage('Name');

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(title, 200)) {
      error.title.errorState = IValidationState.ERROR;
      error.title.errorMessage = errMsgGen.getCharacterLimitMessage('Name', 200);

      isValid = false;
    }

    if (this.props.addError && cyberdyneValidator.isStringGreaterThanLength(fileName, 200)) {
      // CYBERD-2628 fix
      this.props.addError(errMsgGen.getFileNameCharacterLimitMessage(200));
      isValid = false;
    }

    return { isValid, error };
  };

  isValid = () => {
    let isValid = true;
    const files = new Map(this.state.files);

    Array.from(files.keys()).map((fileKey) => {
      const file = files.get(fileKey);
      const isFileValid = this.isValidFile(file, fileKey);

      files.set(fileKey, {
        ...file,
        error: isFileValid.error,
      });

      isValid = isValid && isFileValid.isValid;
    });

    this.setState({
      files,
    });

    return isValid;
  };

  onAdd = () => {
    if (this.isAddDisabled() || !this.isValid()) {
      return;
    }

    const files = this.state.files;

    const caseAttachmentFiles: IAttachment[] = Array.from(files.keys()).map((fileKey) => {
      const file = files.get(fileKey);

      return {
        title: file.title.trim(),
        description: file.description && file.description.trim() ? file.description.trim() : null,
        fileName: fileKey,
        fileSize: humanFileSize(file.details.size),
        fileURL: file.s3SavedUrl,
      };
    });

    this.props.onAdd(caseAttachmentFiles);
  };

  removeFile = (fileKey: string) => {
    const currentFiles = this.state.files;
    const newFiles = new Map(currentFiles);
    newFiles.delete(fileKey);

    this.setState({
      files: newFiles,
    });
  };

  onNameDescriptionChange = (e, key) => {
    const newFiles = new Map(this.state.files);
    newFiles.set(key, {
      ...newFiles.get(key),
      [e.target.name]: e.target.value,
    });

    this.setState({
      files: newFiles,
    });
  };

  trackName = () => {
    track(`${this.props.trackLabel}_ADD_ATTACHMENT_PHOTO_NAME_UPDATED`, {
      'Device Details': getFormattedDeviceDetails(),
    });
  };

  trackDescription = () => {
    track(`${this.props.trackLabel}_ADD_ATTACHMENT_PHOTO_DESCRIPTION_UPDATED`, {
      'Device Details': getFormattedDeviceDetails(),
    });
  };

  getContent = () => (
    <div className={classnames('attachments-modal__content')}>
      <div className={classnames('attachments-modal__add')}>
        <div
          onDragEnter={this.onDragEnter}
          onDragExit={this.onDragExit}
          onDragLeave={this.onDragLeave}
          onDragOver={this.onDragOver}
          onDrop={this.onDrop}
          className={classnames('attachments-modal__drag-drop-box')}
        >
          <span />
          <span>Drag & Drop or </span>
          <label htmlFor="file-upload" className={classnames('label-desktop')}>
            Browse file
          </label>
          <input
            id="file-upload"
            type="file"
            accept={this.getAcceptedFileTypesString()}
            onChange={(e) => this.handleAttachmentChange(e)}
            multiple={true}
          />
        </div>
        <div className={classnames('attachments-modal__or')}>OR</div>
        <CameraButton id="camera" onClick={this.handleAttachmentChange} accept={this.getAcceptedFileTypesString()}>
          <label className={classnames('content-placeholder')} htmlFor="camera">
            <span />
            <span>Camera</span>
          </label>
        </CameraButton>
      </div>
      <div className={classnames('attachments-modal__disclaimer')}>
        <span className={classnames('attachments-modal__disclaimer-icon')} />
        <div className={classnames('attachments-modal__disclaimer-msg')}>
          <span>Maximum file size (each file) is 25MB</span>
          <span>{`Accepted file types are ${this.getAcceptedFileTypesString()}.`}</span>
        </div>
      </div>
      <div className={classnames('attachments-modal__saved-list')}>
        {this.state.files.size > 0 ? (
          Array.from(this.state.files.keys()).map((fileKey, index, map) => {
            const file = this.state.files.get(fileKey);
            const error = file.error;

            return (
              <div key={index} className={classnames('attachments-modal__saved')}>
                {this.getThumb(file)}
                <div className={classnames('attachments-modal__saved-details')}>
                  <div className={classnames('attachments-modal__saved-progress')}>
                    <div className={classnames('attachments-modal__saved-title')}>
                      <span>
                        <b>{fileKey}</b>
                        {`(${humanFileSize(file.details.size)})`}
                      </span>
                      <Input
                        field={{
                          label: ' ',
                          value: null,
                          isRequired: false,
                          type: null,
                        }}
                        className={classnames('attachments-modal__progress')}
                        name=" "
                        width={12}
                        onChange={() => null}
                        error={error.file}
                        renderField={() => (
                          <ProgressBar
                            now={file.progress}
                            variant={
                              error.file.errorState === IValidationState.ERROR
                                ? ButtonStyle.DANGER
                                : ButtonStyle.SUCCESS
                            }
                          />
                        )}
                      />
                    </div>
                    <DeleteButton onClick={() => this.removeFile(fileKey)} />
                  </div>
                  {this.props.hasEditableFields && (
                    <>
                      <Input
                        field={{
                          label: 'Name',
                          value: file.title,
                          isRequired: true,
                          type: CustomFieldType.TEXT,
                        }}
                        placeholder="Attachment name here"
                        name="title"
                        onFocus={this.props.trackLabel ? this.trackName : undefined}
                        onChange={(e) => this.onNameDescriptionChange(e, fileKey)}
                        width={12}
                        error={error.title}
                      />
                      <Input
                        field={{
                          label: 'Description',
                          value: file.description,
                          isRequired: false,
                          type: CustomFieldType.TEXT,
                        }}
                        placeholder="Attachment description here"
                        name="description"
                        onFocus={this.props.trackLabel ? this.trackDescription : undefined}
                        onChange={(e) => this.onNameDescriptionChange(e, fileKey)}
                        width={12}
                        error={error.description}
                      />
                    </>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className={classnames('attachments-modal__saved-list', 'attachments-modal__saved-list--empty')}>
            No photos and attachments uploaded
          </div>
        )}
      </div>
    </div>
  );

  getFooter = () => (
    <div className={classnames('attachments-modal__footer')}>
      <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onClose} content="Cancel" />
      <Button
        bsStyle={ButtonStyle.PRIMARY}
        onClick={this.onAdd}
        content="Add"
        disabled={this.isAddDisabled()}
        showLoader={this.isAddDisabled()}
      />
    </div>
  );

  render() {
    const title = `Add ${this.props.title}`;

    return (
      <div>
        <ModalBase
          show={!this.state.isConfirmModalOpen && this.props.show}
          onClose={(e) => this.onClose(e)}
          titleElement={title}
          bodyElement={this.getContent()}
          footerElement={this.getFooter()}
          className={classnames('attachments-modal privateCSDPModal')}
        />
        <ModalBase
          show={this.state.isConfirmModalOpen}
          onClose={this.onNotConfirmClose}
          titleElement="Close Add Attachments"
          bodyElement="Do you want to close Add Attachments?"
          footerElement={
            <div className={classnames('attachments-modal__footer')}>
              <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onConfirmClose} content="Yes, Close" />
              <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onNotConfirmClose} content="No, Don't Close" />
            </div>
          }
          className={classnames('confirm-modal')}
        />
      </div>
    );
  }
}
